<template>
<div class="nested-entrypoint-wrapper" ref="content"></div>
</template>
<script setup lang="ts">
import { MaybeRef, inject, onBeforeUnmount, ref, toRefs, unref, } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { provideModalData, useModalData } from '#src/js/modalData.mts';

import type { PluginOptions } from '#src/plugins/remoteContentPlugin.mts'
import { useRouter } from 'vue-router';

const props = defineProps<{
    layout: MaybeRef<string | undefined>
    data?: any
}>()

const {_raw: modalProps } = useModalData()
const router = useRouter()

const content = ref()
const wrapper = inject('wrapper')

const LayoutPluginOptions = inject<PluginOptions | undefined>('LayoutCongress', undefined)

if (LayoutPluginOptions === undefined) {
  console.debug("LayoutCongress plugin not initialized correctly. Missing required parameter 'createApp'")
}

// app is set to undefined during onBeforeUnmount.
let app: any | null | undefined = null

watchDebounced([
    // watch for updates on the prop values
    ...Object.values(toRefs(props)),
    // if layout is a ref, make sure to watch for changes to the ref value
    () => unref(props.layout),
    // if we are mounted, unmounted, remounted, unmount and remount on the new element (if any)
    content,
], async () => {
    // if we are unmounting, app is undefined
    if (app === undefined) {
        return
    }

    app?.unmount()
    app = null

    // if layout isn't a string, delay processing until it becomes a string.
    if (props.layout === undefined) {
        return
    }

    const { createApp } = LayoutPluginOptions!

    if (!createApp || typeof createApp !== 'function') {
      throw new Error("Sanity check failed: No init function defined for NestedEntrypoint. Is the layout_congress plugin installed correctly?")
    }

    const _app = createApp({
        setup () {
            const data = props.data ?? {}
            provideModalData({
                ...modalProps,
                transitionAppear: false,
            })
            console.debug("createApp setup function", { data, modalProps })

            return { ...data, }
        },
    })

    _app.use(router)
    _app.provide('wrapper', wrapper)

    const el = unref(content) as HTMLElement | undefined
    const _layout = unref(props.layout)
    // sanity check
    if (el === undefined || _layout === undefined) {
        return
    }

    el.innerHTML = _layout

    app = _app
    try {
        app.mount(el)
    } catch (error) {
        console.error(error)
    }

}, { immediate: true, flush: 'post', debounce: 100, })

onBeforeUnmount(() => {
  app?.unmount()
  app = undefined
})


</script>
<style scoped>
.nested-entrypoint-wrapper {
    display: contents !important;
}
</style>
src/plugins/remoteContentPlugin.mts
